import { Variants } from "./exercise";

/**
 * Single decoded metric value (e.g. `weight`, `meanVelocity`, `roundCount`).
 *
 * Resolves labels and units from exercise metadata by `field`.
 * `value` can be nullable, unlike {@link Measurement.metrics}.
 */
export interface SetMetricValue {
  /** Exercise-metadata field name (e.g. `weight`, `repCount`, `meanVelocity`, `roundCount`). */
  field: string;
  value: number | null;
}

/**
 * How a set measurement was captured. Circuits are always `Manual`.
 */
export const MeasurementValue = {
  Manual: "Manual",
  Sensor: "Sensor",
} as const;

export type MeasurementType = (typeof MeasurementValue)[keyof typeof MeasurementValue];

/**
 * @description
 * Discriminates a circuit measurement from a regular exercise measurement.
 *
 * NB: a different axis from the entity-level `ExerciseType`, where `Output`/`Custom`
 * denote native vs user-created exercises. A measurement only needs `Exercise` vs
 * `Circuit`, and it is optional - absent (`undefined`) is treated as `Exercise`, so
 * existing consumers are unaffected; only circuit-aware callers set `Circuit`.
 */
export enum MeasurementExerciseType {
  Exercise = "Exercise",
  Circuit = "Circuit",
}

export interface MeasurementVideo {
  mux: MuxMeasurementVideo;
}

export interface MuxMeasurementVideo {
  /** ID of the Mux video asset */
  assetId: string;
  /**
   * This is used to determine if the video can be played, downloaded, deleted, etc.
   * We better off waiting for this to be true before allowing any actions on the video.
   */
  mp4Ready?: boolean;
  /** ID of the signed video playback */
  playbackId: string;
}

export interface Measurement {
  id: string;
  athleteId: string;
  exerciseId: string;
  completedWorkoutId: null | string;
  completedDate: string;
  variant: null | Variants;
  /**
   * @description
   * Discriminates circuit measurements from exercise measurements. Optional;
   * absent is treated as `Exercise`, so existing consumers are unaffected.
   */
  exerciseType?: MeasurementExerciseType;
  metrics: {
    field: string;
    value: number;
  }[];
  note?: string | null;
  repetitions: {
    field: string;
    value: number;
  }[][];
  /**
   * @description
   * Per-round values for a circuit measurement (same shape as `repetitions`).
   * Populated when the circuit has an input, empty when the input is `None`.
   */
  rounds?: {
    field: string;
    value: number;
  }[][];
  /**
   * @description
   * Number of rounds completed for a circuit measurement. Always present and
   * `> 0` for circuits. NB: `rounds` is not a reliable round count - use this.
   */
  roundCount?: number;
  video: MeasurementVideo | null;
}

export interface MeasurementSet {
  completedDate: string;
  measurementId: string;
  repetitionCount: number;
  setNumber: number;
  variant: null | "Left" | "Right";
  weightValue: number;
}

export interface FireStoreMeasurementBase {
  id: string;
  athleteId: string;
  userId: string;
  completedDate: string;
  exerciseId: string;
  variant?: null | "Left" | "Right";
  /** Discriminates circuit measurements from exercise measurements (absent ⇒ `Exercise`). */
  exerciseType?: MeasurementExerciseType;
  reps?: { [repMetric: string]: any }[];
  /**
   * @description
   * Per-round values for a circuit measurement (same shape as `reps`). Populated
   * when the circuit has an input, empty when the input is `None`.
   */
  rounds?: { [roundMetric: string]: any }[];
  /** Number of rounds completed for a circuit measurement (always present and `> 0` for circuits). */
  roundCount?: number;
  video: MeasurementVideo | null;
}

export interface FireStoreMeasurement extends FireStoreMeasurementBase {
  [metric: string]: any;
}
